import { ColumnElement } from '@atoms/columnElement';
import { VariableSelector } from '@atoms/dataSelectors/variableSelector/variableSelector';
import { ExempleTooltip, InfoTooltip } from '@atoms/infoTooltip';
import { SwitchV2 } from '@atoms/switch';
import { Column } from '@components/dnd/base/blockBase/body.block.style';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle
} from '@components/ui/card';
import { Input } from '@components/ui/input';
import { Label } from '@components/ui/label';
import type { AtomModel } from '@models/atom.model';
import type { DateData } from '@stratumn/studio-shared';
import { observer } from 'mobx-react';

type Props = Pick<
  DateData,
  'deadlineWarningBuffer' | 'isDeadline' | 'doneVariableReference'
> & {
  onDeadlineWarningBufferChange: (
    deadlineWarningBuffer: DateData['deadlineWarningBuffer']
  ) => void;
  onIsDeadlineChange: (isDeadline: DateData['isDeadline']) => void;
  onDoneVariableReferenceChange: (
    doneVariableReference: DateData['doneVariableReference']
  ) => void;
  atomId: AtomModel['id'];
};

export const DateWidgetSettings = observer((props: Props) => {
  const onBufferDaysChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (!value) {
      props.onDeadlineWarningBufferChange(undefined);
      return;
    }

    props.onDeadlineWarningBufferChange(Number(value));
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Deadline settings</CardTitle>
        <CardDescription>
          The date can be shown in a different color if it's a deadline, and the
          deadline has either been missed or is within the buffer period.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex gap-6">
          <Column $gap="1rem" $width="220px">
            <ColumnElement>
              <SwitchV2
                checked={props.isDeadline}
                id={`deadline-${props.atomId}`}
                labelText="Display a deadline"
                onCheckedChange={props.onIsDeadlineChange}
              />
            </ColumnElement>

            <ColumnElement>
              <div className="flex items-center gap-2">
                <Label>Done condition</Label>
                <InfoTooltip
                  additionalContent={
                    <ExempleTooltip
                      input="Trace Terminated: true"
                      output="No deadline"
                    />
                  }
                  description="The selected variable will be used to determine if the task is done or not. If the variable exist, and does not equal to false, the deadline will be disabled."
                  title="Done condition"
                />
              </div>
              <VariableSelector
                disabled={!props.isDeadline}
                onSelected={props.onDoneVariableReferenceChange}
                selectedRef={props.doneVariableReference}
              />
            </ColumnElement>
          </Column>
          <Column $width="220px">
            <ColumnElement>
              <div className="flex w-full items-center gap-2">
                <Label htmlFor={`deadline-warning-buffer-${props.atomId}`}>
                  Deadline warning buffer
                </Label>
                <InfoTooltip
                  description="The number of days before the deadline to display a warning message"
                  title="Deadline warning buffer"
                />
              </div>

              <Input
                className="w-full"
                disabled={!props.isDeadline}
                id={`deadline-warning-buffer-${props.atomId}`}
                onChange={onBufferDaysChange}
                placeholder="0 (default)"
                type="number"
                value={props.deadlineWarningBuffer}
              />
            </ColumnElement>
          </Column>
        </div>
      </CardContent>
    </Card>
  );
});
